import React from 'react';
import PropTypes from 'prop-types';
import { ReportModuleBlockingError } from '@americanexpress/travel-error-base';
import { PageLevelError } from '@americanexpress/consumer-travel-base';
import { FormattedMessage, IntlProvider } from 'react-intl';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { ProgressCircular } from '@americanexpress/dls-react';
import { useLoadLanguagePack } from '../hooks/useLoadLanguagePack';
import { useTravelerData } from '../hooks/useTravelerData';
import { MODULE_NAME } from '../constants/modules';
import { useLocaleBasedRedirect } from '../hooks/useLocaleBasedRedirect';
import { getTravelerLocale, isCenturionEligible } from '../utils/travelerDataUtils';
import { hasCenturionRule } from '../constants/featureRulesConstants';
import isCenturionContext from '../context/isCenturionContext';
import SwitchFlyErrorHandler from './SwitchFlyErrorHandler/SwitchFlyErrorHandler';
import HomePage from './HomePage';
import { useOneCms } from '../fetchye/useOneCmsFetchye';

const TravelHomeWrapper = ({
  languageData,
  locale,
  location,
  router,
}) => {
  useLoadLanguagePack();
  const hasCenturion = useConditionalFeature(hasCenturionRule);
  const { travelerData } = useTravelerData();
  const { isLoading, error } = useOneCms();
  const isRedirecting = useLocaleBasedRedirect(router);

  const moduleLoading = Object.entries(languageData).length === 0
    || isLoading
    || isRedirecting;

  if (moduleLoading) {
    return (
      <div className="text-align-center pad">
        <ProgressCircular size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ReportModuleBlockingError
        moduleID={MODULE_NAME}
        otherData={{
          causes: {
            homePageContent: !!error,
          },
        }}
      >
        <IntlProvider locale={locale} messages={languageData}>
          <PageLevelError
            description={<FormattedMessage id="error.module_load" />}
          />
        </IntlProvider>
      </ReportModuleBlockingError>
    );
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <isCenturionContext.Provider
        value={isCenturionEligible(hasCenturion, travelerData)}
      >
        <SwitchFlyErrorHandler location={location} />
        <HomePage location={location} />
        <div className="display-none">
          <div className="automation-travel-home-card-locale">
            {getTravelerLocale(travelerData)}
          </div>
          <div className="automation-travel-home-card-ia-code">
            {travelerData?.card?.cardIdCode}
          </div>
        </div>
      </isCenturionContext.Provider>
    </IntlProvider>
  );
};

TravelHomeWrapper.propTypes = {
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({}).isRequired,
  travelerData: PropTypes.shape({
    card: PropTypes.shape({
      type: PropTypes.string,
      locale: PropTypes.string,
      cardIdCode: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

TravelHomeWrapper.defaultProps = {
  travelerData: null,
  location: {
    pathname: null,
  },
};

export const TestableTravelHomeWrapper = TravelHomeWrapper;
export default oneAppModuleWrapper(MODULE_NAME)(TravelHomeWrapper);
